import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/ui/button.tsx";
import { useDiaryStore } from "@/lib/stores/diaryStore";
import { useAuthStore } from "@/lib/stores/authStore";
import { useLocale } from "@/contexts/IntlContext";
import ConfirmationModal from "@/components/ui/confirmation-modal";
import { ExerciseLayout } from "@/components/ui/exercise-layout";
import note1 from "@/assets/note-1.png";
import note2 from "@/assets/note-2.png";
import note3 from "@/assets/note-3.png";
import note4 from "@/assets/note-4.png";
import note5 from "@/assets/note-5.png";
import note6 from "@/assets/note-6.png";
import note7 from "@/assets/note-7.png";
import note8 from "@/assets/note-8.png";
import note9 from "@/assets/note-9.png";
import happyIcon from "@/assets/icons/happy-icon.svg";
import sadIcon from "@/assets/icons/sad-icon.svg";
import cherfulIcon from "@/assets/icons/cherful-icon.svg";
import angryIcon from "@/assets/icons/angry-icon.svg";
import boredIcon from "@/assets/icons/bored-icon.svg";
import { Information } from "iconsax-reactjs";
export const Route = createFileRoute(
  "/(content)/adults/exercises/(shared)/diary-adults/exercise"
)({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: "Writing a Diary - HAMD",
      },
    ],
  }),
  beforeLoad: () => ({
    title: "weakness.exercises.diary",
    navbar: {
      showTitle: true,
    },
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    templateId: search.templateId as number | undefined,
  }),
});

const notePages = {
  1: note1,
  2: note2,
  3: note3,
  4: note4,
  5: note5,
  6: note6,
  7: note7,
  8: note8,
  9: note9,
};

export const feelings = [
  { id: "happy", icon: happyIcon, label: "Happy", color: "#4D78F9" },
  { id: "cheerful", icon: cherfulIcon, label: "Cheerful", color: "#B738CD" },
  { id: "sad", icon: sadIcon, label: "Sad", color: "#E3EE0C" },
  { id: "angry", icon: angryIcon, label: "Angry", color: "#E08535" },
  { id: "bored", icon: boredIcon, label: "Bored", color: "#D83087" },
];

function RouteComponent() {
  const [response, setResponse] = useState("");
  const [selectedFeeling, setSelectedFeeling] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const isGoingToRegisterRef = useRef(false);
  const { templateId } = useSearch({
    from: "/(content)/adults/exercises/(shared)/diary-adults/exercise",
  });
  const { addEntry, isLoading, error, clearError } = useDiaryStore();
  const { user, setRedirectAfterAuth } = useAuthStore();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const intl = useIntl();

  const selectedNotePage =
    notePages[templateId as keyof typeof notePages] || note1;

  const getCurrentMonthName = () => {
    const localeString = locale === "ar" ? "ar" : "en-US";
    const currentDate = new Date();

    return currentDate.toLocaleDateString(localeString, {
      month: "long",
    });
  };

  useEffect(() => {
    if (!templateId) {
      navigate({
        to: "/adults/exercises/diary-adults/diary-template",
        replace: true,
      });
    }
  }, [templateId, navigate]);

  useEffect(() => {
    if (user && typeof window !== "undefined") {
      const pendingEntry = localStorage.getItem("pendingDiaryEntry");
      if (pendingEntry && pendingEntry.trim()) {
        try {
          const entryData = JSON.parse(pendingEntry);
          if (entryData.content) {
            setResponse(entryData.content);
            if (entryData.feeling) {
              setSelectedFeeling(entryData.feeling);
            }
          }
        } catch {
          setResponse(pendingEntry.trim());
        }

        const timeoutId = setTimeout(() => {
          const currentPath = window.location.pathname;

          if (currentPath.includes("/diary-adults/exercise")) {
            navigate({
              to: "/adults/exercises/diary-adults/archive",
              search: { saved: true },
            });
          }
        }, 500);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [user, navigate]);

  const handleSave = async () => {
    if (!user) {
      if (response.trim() && typeof window !== "undefined") {
        const entryData = {
          content: response.trim(),
          feeling: selectedFeeling,
        };
        localStorage.setItem("pendingDiaryEntry", JSON.stringify(entryData));
      }
      setRedirectAfterAuth(
        "/adults/exercises/diary-adults/archive?saved=true"
      );
      setShowAuthModal(true);
      return;
    }

    try {
      clearError();
      await addEntry(response, selectedFeeling || undefined, true);

      setResponse("");
      setSelectedFeeling(null);

      navigate({
        to: "/adults/exercises/diary-adults/archive",
        search: { saved: true },
      });
    } catch (error) {
      console.error("Error saving diary entry:", error);
    }
  };

  const handleRegisterRedirect = () => {
    isGoingToRegisterRef.current = true;
    navigate({ to: "/auth/register" });
  };

  return (
    <ExerciseLayout
      contentClassName="py-7.5 space-y-18"
      actions={
        <Button
          className="w-full"
          onClick={handleSave}
          disabled={isLoading || !response.trim()}
        >
          {isLoading ? (
            <FormattedMessage id="diary.exercise.saving" />
          ) : (
            <FormattedMessage id="diary.exercise.saveEntry" />
          )}
        </Button>
      }
    >
      <div className="space-y-4 pb-6 px-2">
        {error && (
          <div className="text-small text-red-500 bg-red-50 p-3 rounded-lg border border-red-200">
            {error}
          </div>
        )}

        <div className="flex justify-center items-center bg-taupe p-2.5 rounded-[12px]">
          <h3 className="text-white text-center">{getCurrentMonthName()}</h3>
        </div>

        <div className="relative w-full max-w-2xl mx-auto">
          <div className="relative w-full" style={{ aspectRatio: "8.5 / 11" }}>
            <img
              src={selectedNotePage}
              alt="Notebook page"
              className="absolute inset-0 w-full h-full object-contain "
            />

            <div className="absolute inset-0 pl-15 md:pl-22 md:p-8 p-5 flex flex-col flex-shrink-0">
              <div className="flex justify-around gap-2 pb-4 flex-wrap">
                {feelings.map((feeling) => (
                  <div
                    key={feeling.id}
                    className="flex flex-col items-center cursor-pointer group"
                    onClick={() => setSelectedFeeling(feeling.id)}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                        selectedFeeling === feeling.id
                          ? "scale-110"
                          : "bg-transparent hover:bg-gray-100 group-hover:scale-105"
                      }`}
                      style={{
                        backgroundColor:
                          selectedFeeling === feeling.id
                            ? `${feeling.color}20`
                            : "transparent",
                        borderColor:
                          selectedFeeling === feeling.id
                            ? feeling.color
                            : "transparent",
                        borderWidth:
                          selectedFeeling === feeling.id ? "2px" : "0px",
                      }}
                    >
                      <img
                        src={feeling.icon}
                        alt={feeling.label}
                        className="w-6 h-6 transition-all duration-200"
                        style={{
                          filter:
                            selectedFeeling === feeling.id
                              ? `brightness(1.1) saturate(1.2)`
                              : "none",
                        }}
                      />
                    </div>
                    <span
                      className={`text-xs transition-all duration-200 font-medium ${
                        selectedFeeling === feeling.id
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                      style={{
                        color:
                          selectedFeeling === feeling.id
                            ? feeling.color
                            : "#6B7280",
                      }}
                    >
                      {feeling.label}
                    </span>
                  </div>
                ))}
              </div>
              <textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "diary.exercise.placeholder",
                })}
                className="w-full flex-1 bg-transparent resize-none focus:outline-none text-small placeholder:text-gray"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
        <div className="bg-sage p-2.5 rounded-[15px]">
          <p className="text-micro text-white flex items-center gap-2.5">
            <Information className="text-white" size={24} />
            <FormattedMessage id="diary.exercise.privacyNotice" />
          </p>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          if (!isGoingToRegisterRef.current && typeof window !== "undefined") {
            localStorage.removeItem("pendingDiaryEntry");
          }
          isGoingToRegisterRef.current = false;
        }}
        onConfirm={handleRegisterRedirect}
        titleKey="common.auth.title"
        messageKey="common.auth.message"
        confirmButtonTextKey="common.auth.register"
        cancelButtonTextKey="common.cancel"
      />
    </ExerciseLayout>
  );
}
